import {
  ActionButton,
  AnyError,
  BodyText,
  getErrorMessage,
  PageActions,
  UnsavedChangesGuard,
  useSnackbar,
} from '@if/mui'
import {
  isLoanLockNotHeldError,
  useApplyCalendarSimulation,
  useRefetchCalendarSimulation,
  useReplayCalendarActions,
} from '@src/api/loan/loan-api'
import { SYSTEM_CONSTANT_CHECK_FOR_STALE_STATE_INTERVAL } from '@src/types/constants'
import { CalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { isAxiosError } from 'axios'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import LoanCalendarChangeLog from './LoanCalendarChangeLog'
import { useSaveConflictDialog } from './SaveConflictDialog-hook'
import { useStaleSimulationDialog } from './StaleSimulationDialog-hook'

export enum LoanCalendarSimulationStatus {
  NoChanges = 'no_changes',
  HasChanges = 'has_changes',
  SavePending = 'save_pending',
}

export interface LoanCalendarSimulationState {
  originalSimulation: SignedCalendarSimulation
  currentSimulation: SignedCalendarSimulation
  actions: CalendarSimulationAction[]
  status: LoanCalendarSimulationStatus
}

interface LoanCalendarSimulationProviderProps {
  children?: ReactNode
  originalSignedSimulation: SignedCalendarSimulation
}

// should never be exported. Only meant for internal state management
enum LoanCalendarSimulationStateActionType {
  Reset = 'reset',
  AddSimulationAction = 'add_simulation_action',
  ReplayActions = 'replay_actions',
  SavePending = 'save_pending',
  SaveSuccess = 'save_success',
  SaveError = 'save_error',
}

interface BaseSimulationAction<T extends LoanCalendarSimulationStateActionType> {
  type: T
}

interface SaveSuccessSimulationAction extends BaseSimulationAction<LoanCalendarSimulationStateActionType.SaveSuccess> {
  payload: SignedCalendarSimulation
}

interface AddSimulationAction extends BaseSimulationAction<LoanCalendarSimulationStateActionType.AddSimulationAction> {
  payload: SignedCalendarSimulation
}

interface ReplayActionsSimulationAction
  extends BaseSimulationAction<LoanCalendarSimulationStateActionType.ReplayActions> {
  payload: SignedCalendarSimulation
}

type SimulationAction =
  | AddSimulationAction
  | ReplayActionsSimulationAction
  | SaveSuccessSimulationAction
  | BaseSimulationAction<LoanCalendarSimulationStateActionType.Reset>
  | BaseSimulationAction<LoanCalendarSimulationStateActionType.SaveError>
  | BaseSimulationAction<LoanCalendarSimulationStateActionType.SavePending>

const SimulationReducer = (
  state: LoanCalendarSimulationState,
  action: SimulationAction,
): LoanCalendarSimulationState => {
  switch (action.type) {
    case LoanCalendarSimulationStateActionType.AddSimulationAction: {
      if (!action.payload) throw new Error('AddSimulationAction requires a payload')

      return {
        ...state,
        actions: action.payload.simulation.actions,
        currentSimulation: action.payload,
        status: LoanCalendarSimulationStatus.HasChanges,
      }
    }
    case LoanCalendarSimulationStateActionType.ReplayActions: {
      if (!action.payload) throw new Error('ReplayActions requires a payload')

      // Undoing every action is Cancel by another route, so it lands on the same state: the
      // calendar as the page loaded it, with nothing left for the footer to offer to save.
      const { actions } = action.payload.simulation
      if (actions.length === 0) {
        return {
          ...state,
          actions: [],
          currentSimulation: state.originalSimulation,
          status: LoanCalendarSimulationStatus.NoChanges,
        }
      }

      return {
        ...state,
        actions,
        currentSimulation: action.payload,
        status: LoanCalendarSimulationStatus.HasChanges,
      }
    }
    case LoanCalendarSimulationStateActionType.SavePending:
      return { ...state, status: LoanCalendarSimulationStatus.SavePending }
    case LoanCalendarSimulationStateActionType.Reset:
      return {
        ...state,
        actions: [],
        status: LoanCalendarSimulationStatus.NoChanges,
        currentSimulation: state.originalSimulation,
      }
    case LoanCalendarSimulationStateActionType.SaveSuccess:
      if (!action.payload) throw new Error('SaveSuccess requires a payload')
      return {
        actions: [],
        status: LoanCalendarSimulationStatus.NoChanges,
        originalSimulation: action.payload,
        currentSimulation: action.payload,
      }
    case LoanCalendarSimulationStateActionType.SaveError:
      return {
        ...state,
        status: LoanCalendarSimulationStatus.HasChanges,
      }
    default:
      return state
  }
}

export interface LoanCalendarSimulationContextProps extends LoanCalendarSimulationState {
  addSimulationAction: (action: SignedCalendarSimulation) => void
  refetch: () => void
}

const LoanCalendarSimulationContext = createContext<LoanCalendarSimulationContextProps | null>(null)

export const useLoanCalendarSimulation = () => {
  const context = useContext(LoanCalendarSimulationContext)
  if (!context) {
    throw new Error('useLoanCalendarSimulation must be used within a LoanCalendarSimulationProvider')
  }
  return context
}

/**
 * Holds the pending calendar actions. Mounted by the page above its tabs, so switching to another tab and back
 * keeps them.
 */
const LoanCalendarSimulationProvider = ({
  children,
  originalSignedSimulation,
}: LoanCalendarSimulationProviderProps) => {
  const { t } = useTranslation()
  const [simulationState, dispatch] = useReducer(SimulationReducer, {
    actions: [],
    originalSimulation: originalSignedSimulation,
    currentSimulation: originalSignedSimulation,
    status: LoanCalendarSimulationStatus.NoChanges,
  })
  const { showSuccess, showError } = useSnackbar()
  const loanId = originalSignedSimulation.simulation.fullLoan.loan.id
  const financingCompanyId = originalSignedSimulation.simulation.fullLoan.loan.financingCompanyId
  const refetch = useRefetchCalendarSimulation(loanId, financingCompanyId)
  const { mutateAsync: applySimulation } = useApplyCalendarSimulation(loanId, financingCompanyId)
  const { mutateAsync: replayActions, isPending: isUndoing } = useReplayCalendarActions(loanId, financingCompanyId)

  const hasPendingChanges = simulationState.status !== LoanCalendarSimulationStatus.NoChanges
  const isSaving = simulationState.status === LoanCalendarSimulationStatus.SavePending

  const applyRefreshedSimulation = useCallback((refreshed: SignedCalendarSimulation) => {
    dispatch({ type: LoanCalendarSimulationStateActionType.AddSimulationAction, payload: refreshed })
  }, [])

  const { open: openStaleDialog } = useStaleSimulationDialog(
    simulationState.currentSimulation,
    applyRefreshedSimulation,
  )
  const { open: openConflictDialog } = useSaveConflictDialog(refetch)

  useEffect(() => {
    if (!hasPendingChanges) return

    const checkInterval = setInterval(() => {
      if (simulationState.currentSimulation.simulation.isExpired()) void openStaleDialog()
    }, SYSTEM_CONSTANT_CHECK_FOR_STALE_STATE_INTERVAL)
    return () => clearInterval(checkInterval)
  }, [hasPendingChanges, simulationState.currentSimulation.simulation, openStaleDialog])

  const undoToAction = useCallback(
    async (index: number) => {
      const { simulation } = simulationState.currentSimulation
      try {
        const replayed = await replayActions({ simulation, actions: simulation.actions.slice(0, index) })
        dispatch({ type: LoanCalendarSimulationStateActionType.ReplayActions, payload: replayed })
      } catch (error: unknown) {
        showError(getErrorMessage(error as AnyError))
      }
    },
    [replayActions, simulationState.currentSimulation, showError],
  )

  const saveHandler = async () => {
    if (simulationState.status !== LoanCalendarSimulationStatus.HasChanges)
      throw new Error('Save requires a currentSimulationWithSignature')

    if (simulationState.currentSimulation.simulation.isExpired()) {
      void openStaleDialog()
      return
    }

    dispatch({ type: LoanCalendarSimulationStateActionType.SavePending })

    try {
      const result = await applySimulation(simulationState.currentSimulation)
      dispatch({ type: LoanCalendarSimulationStateActionType.SaveSuccess, payload: result })
      showSuccess()
    } catch (error: unknown) {
      if (isLoanLockNotHeldError(error)) {
        // The loan did not necessarily change: keep the pending actions so the user can take the lock back and save
        dispatch({ type: LoanCalendarSimulationStateActionType.SaveError })
        showError(t('loan.calendar.lockNotHeld'))
      } else if (isAxiosError(error) && error.response?.status === 409) {
        void openConflictDialog()
      } else {
        dispatch({ type: LoanCalendarSimulationStateActionType.SaveError })
        showError(getErrorMessage(error as AnyError))
      }
    }
  }

  const ctxValue = useMemo<LoanCalendarSimulationContextProps>(
    () => ({
      ...simulationState,
      addSimulationAction: (action: SignedCalendarSimulation) => {
        dispatch({ type: LoanCalendarSimulationStateActionType.AddSimulationAction, payload: action })
      },
      refetch,
    }),
    [simulationState, refetch],
  )

  return (
    <LoanCalendarSimulationContext.Provider value={ctxValue}>
      <UnsavedChangesGuard active={hasPendingChanges} />
      {hasPendingChanges && (
        <PageActions
          info={
            isSaving ? (
              <BodyText>{t('loan.calendar.pendingChanges.saving')}</BodyText>
            ) : (
              <LoanCalendarChangeLog actions={simulationState.actions} onUndoTo={undoToAction} loading={isUndoing} />
            )
          }
        >
          <ActionButton
            disabled={isSaving || isUndoing}
            onClick={() => dispatch({ type: LoanCalendarSimulationStateActionType.Reset })}
          >
            {t('common.cancel')}
          </ActionButton>
          <ActionButton emphasis="principal" loading={isSaving} disabled={isUndoing} onClick={() => void saveHandler()}>
            {t('common.save')}
          </ActionButton>
        </PageActions>
      )}
      {children}
    </LoanCalendarSimulationContext.Provider>
  )
}

export default LoanCalendarSimulationProvider
